import {TEST_IDS} from '@github-ui/issue-body/constants/test-ids'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {ChevronDownIcon, ChevronUpIcon, ProjectTemplateIcon, TableIcon} from '@primer/octicons-react'
import {IconButton, Link, Truncate} from '@primer/react'
import {useCallback} from 'react'
import {fetchQuery, graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import type {ProjectItemSectionFieldListQuery} from './__generated__/ProjectItemSectionFieldListQuery.graphql'
import type {ProjectItemSectionView$key} from './__generated__/ProjectItemSectionView.graphql'
import {ClosedProjectIcon} from './ClosedProjectIcon'
import {SingleSelectField} from './fields/SingleSelectField'
import {ProjectItemSectionFieldListGraphqlQuery} from './ProjectItemSectionFieldList'
import styles from './ProjectItemSectionView.module.css'

export type ProjectItemSectionViewProps = {
  projectItem: ProjectItemSectionView$key
  open: boolean
  setOpen: (isOpen: boolean) => void
  onIssueUpdate: () => void
  isSelectedProject: boolean
  isProjectClosed: boolean
}

export function ProjectItemSectionView({
  projectItem,
  setOpen,
  open,
  onIssueUpdate,
  isSelectedProject,
  isProjectClosed,
}: ProjectItemSectionViewProps) {
  const environment = useRelayEnvironment()
  const {id, project, fieldValueByName, isArchived} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ProjectItemSectionView on ProjectV2Item {
        id
        isArchived
        project {
          id
          title
          template
          viewerCanUpdate
          url
          field(name: "Status") {
            ...SingleSelectFieldConfigFragment @dangerously_unaliased_fixme
          }
        }
        fieldValueByName(name: "Status") {
          ...SingleSelectFieldFragment @dangerously_unaliased_fixme
        }
      }
    `,
    projectItem,
  )

  const prefetchFieldList = useCallback(() => {
    if (!open) {
      fetchQuery<ProjectItemSectionFieldListQuery>(
        environment,
        ProjectItemSectionFieldListGraphqlQuery,
        {
          id,
        },
        {fetchPolicy: 'store-or-network', networkCacheConfig: {metadata: {ttl: DEFAULT_RELAY_CACHE_TTL}}},
      ).subscribe({})
    }
  }, [environment, id, open])

  const projectIcon = isProjectClosed ? (
    <ClosedProjectIcon className={styles.projectIcon} />
  ) : project.template ? (
    <ProjectTemplateIcon className={styles.projectIcon} aria-hidden="true" />
  ) : (
    <TableIcon className={styles.projectIcon} aria-hidden="true" />
  )

  return (
    <div className={styles.ProjectItemSectionViewContainer}>
      {isSelectedProject ? (
        <span className={styles.projectTitleText}>
          {projectIcon}
          <span className={styles.truncatedProjectTitle}>{project.title}</span>
          {isProjectClosed && <span className="sr-only">Closed</span>}
        </span>
      ) : (
        <div className={styles.projectLinkContainer}>
          <Link href={project.url} hoverColor={'accent.fg'} tabIndex={0} className={styles.projectLink}>
            {projectIcon}
            <Truncate title={project.title} data-testid={TEST_IDS.projectTitle} className={styles.Truncate}>
              {project.title}
            </Truncate>
            {isProjectClosed && <span className={styles.closedLabel}>Closed</span>}
          </Link>
        </div>
      )}
      <div className={styles.statusFieldContainer}>
        {project.field && fieldValueByName !== undefined && (
          <SingleSelectField
            viewerCanUpdate={project.viewerCanUpdate && !isArchived}
            itemId={id}
            projectId={project.id}
            field={project.field}
            value={fieldValueByName}
            onIssueUpdate={onIssueUpdate}
            isStatusField
          />
        )}
        <IconButton
          variant="invisible"
          onClick={() => {
            setOpen(!open)
          }}
          onPointerEnter={prefetchFieldList}
          size="small"
          icon={!open ? ChevronDownIcon : ChevronUpIcon}
          aria-label={!open ? LABELS.showMoreProjectFields : LABELS.hideMoreProjectFields}
          aria-expanded={open}
        />
      </div>
    </div>
  )
}
